import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { useJob, useJobContacts, useJobEvents } from '@/hooks/useDispatcher';
import { formatDateTime, formatDuration } from '@/lib/utils';

export default function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const job = useJob(id);
  const events = useJobEvents(id);
  const contacts = useJobContacts(id);

  return (
    <div>
      <Link to="/jobs" className="text-xs text-gray-500 hover:text-brand-dark inline-flex items-center gap-1 mb-3">
        <ArrowLeft size={12} /> Retour aux jobs
      </Link>
      <PageHeader
        title={job.data ? `${job.data.industry} — ${job.data.location_query}` : '…'}
        description={job.data ? `Source: ${job.data.source} · Job ${job.data.source_job_id}` : undefined}
        action={job.data ? <StatusBadge status={job.data.status} /> : null}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="VM" value={job.data?.vm_slug ?? '—'} />
        <Stat label="Contacts" value={String(job.data?.contacts_count ?? '—')} />
        <Stat label="ETA" value={formatDuration(job.data?.eta_ms)} />
        <Stat label="Démarré" value={formatDateTime(job.data?.started_at)} />
      </div>

      <Tabs defaultValue="timeline">
        <TabsList>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
          <TabsTrigger value="payload">Payload</TabsTrigger>
        </TabsList>

        <TabsContent value="timeline">
          <Card>
            {events.isLoading ? (
              <CardContent>
                <Skeleton className="h-32 w-full" />
              </CardContent>
            ) : !events.data?.length ? (
              <EmptyState title="Pas encore d'évènement" />
            ) : (
              <ol className="divide-y divide-brand-border">
                {events.data.map((e) => (
                  <li key={e.id} className="px-5 py-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-sm font-medium text-brand-dark">{e.type}</div>
                        {e.message && <div className="text-xs text-gray-500 mt-0.5">{e.message}</div>}
                      </div>
                      <span className="text-xs text-gray-400 shrink-0">{formatDateTime(e.ts)}</span>
                    </div>
                    {e.payload && (
                      <pre className="mt-2 bg-gray-50 border border-brand-border rounded-brand px-3 py-2 text-[11px] text-gray-700 overflow-x-auto">
                        {JSON.stringify(e.payload, null, 2)}
                      </pre>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="contacts">
          <Card>
            <CardHeader title="Contacts produits" subtitle={`${contacts.data?.length ?? 0} entrées`} />
            {contacts.isLoading ? (
              <CardContent>
                <Skeleton className="h-32 w-full" />
              </CardContent>
            ) : !contacts.data?.length ? (
              <EmptyState title="Aucun contact pour ce job" />
            ) : (
              <div className="divide-y divide-brand-border">
                {contacts.data.map((c) => (
                  <Link
                    key={c.id}
                    to={`/contacts/${c.id}`}
                    className="flex items-center justify-between px-5 py-3 table-row-hover"
                  >
                    <div>
                      <div className="text-sm font-medium text-brand-dark">{c.company_name ?? '—'}</div>
                      <div className="text-xs text-gray-400">
                        {c.email ?? c.company_website ?? c.domain ?? '—'}
                      </div>
                    </div>
                    <ChevronRight size={14} className="text-gray-300" />
                  </Link>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="payload">
          <Card>
            <CardContent>
              <pre className="bg-gray-50 border border-brand-border rounded-brand px-4 py-3 text-xs text-gray-700 overflow-x-auto">
                {job.data ? JSON.stringify(job.data, null, 2) : 'Loading…'}
              </pre>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card className="p-4">
      <div className="text-xs text-gray-400">{label}</div>
      <div className="font-heading text-brand-dark text-base mt-1 truncate">{value}</div>
    </Card>
  );
}
